import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { api, relative } from '@/lib/api';

interface Notification {
  id: string;
  title: string;
  body: string | null;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

export function NotificationBell() {
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = () =>
    api
      .get<{ notifications: Notification[]; unreadCount: number }>('/api/notifications')
      .then((r) => {
        setItems(r.notifications);
        setUnread(r.unreadCount);
      })
      .catch(() => undefined);

  useEffect(() => {
    void load();
    // Polling rather than websockets: with four to eight users a 60-second
    // refresh is entirely adequate and avoids a socket layer to operate.
    const timer = setInterval(() => void load(), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markRead = async () => {
    await api.post('/api/notifications/read').catch(() => undefined);
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => {
          setIsOpen((v) => !v);
          if (!isOpen && unread > 0) void markRead();
        }}
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-ink-secondary transition-colors duration-fast hover:bg-surface-active hover:text-ink"
        aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
      >
        <svg aria-hidden viewBox="0 0 20 20" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15.5 8.5a5.5 5.5 0 0 0-11 0c0 4-1.5 5.5-1.5 5.5h14s-1.5-1.5-1.5-5.5ZM8.3 17a2 2 0 0 0 3.4 0" />
        </svg>
        {/* The orange dot — the key indicator for unread alerts. */}
        {unread > 0 && (
          <span
            aria-hidden
            className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-surface"
          />
        )}
      </button>

      {isOpen && (
        <div className="card absolute right-0 top-full z-30 mt-2 w-80 !shadow-lg animate-slide-down">
          <div className="border-b border-rule px-4 py-2.5">
            <span className="label">Notifications</span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-4 font-sans text-caption text-ink-tertiary">Nothing to report.</p>
            ) : (
              items.map((n) => {
                const content = (
                  <>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={clsx('text-body', n.isRead ? 'text-ink-secondary' : 'text-ink')}>
                        {n.title}
                      </span>
                      <span className="shrink-0 font-sans text-caption text-ink-tertiary">
                        {relative(n.createdAt)}
                      </span>
                    </div>
                    {n.body && <p className="mt-0.5 text-caption text-ink-tertiary">{n.body}</p>}
                  </>
                );
                return n.link ? (
                  <Link
                    key={n.id}
                    to={n.link}
                    onClick={() => setIsOpen(false)}
                    className="block border-b border-divider px-4 py-3 transition-colors last:border-b-0 hover:bg-secondary/[0.05] dark:hover:bg-secondary/10"
                  >
                    {content}
                  </Link>
                ) : (
                  <div key={n.id} className="border-b border-rule/50 px-4 py-3 last:border-b-0">
                    {content}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
